import { useRef, useState } from 'react'
import {
  ChartShell, ChartTooltip, axisFormatter, clip, colorVar, formatChartValue, niceScale, pointerIndex, seriesTable, summarize, textWidth, thinIndices, useTweened, useTweenedDomain,
  type ChartSeries, type ChartUnit, type TooltipRow,
} from './chartKit'

export interface BarChartProps {
  categories: string[]
  categoryTitles?: string[] | undefined
  series: ChartSeries[]
  /** Barre impilate invece che raggruppate. */
  stacked?: boolean | undefined
  /** Barre orizzontali (una riga per categoria). */
  horizontal?: boolean | undefined
  /** Linee sovrapposte sulla stessa scala (solo barre verticali), per esempio un saldo. */
  lines?: ChartSeries[] | undefined
  unit?: ChartUnit | undefined
  height?: number | undefined
  loading?: boolean | undefined
  label: string
  categoryHeading?: string | undefined
  emptyTitle?: string | undefined
  emptyDescription?: string | undefined
}

const PAD = { top: 10, right: 12, bottom: 26 }

export function BarChart(p: BarChartProps) {
  const { categories, series, stacked, horizontal, unit = 'number' } = p
  const lines = horizontal ? [] : (p.lines ?? [])
  const n = categories.length
  const everySeries = [...series, ...lines]
  const empty = n === 0 || everySeries.every(s => s.values.every(v => v == null))
  const [active, setActive] = useState<number | null>(null)
  const touch = useRef(false)

  // Dominio: per le impilate le somme positive/negative, per le raggruppate i singoli valori.
  let dMin = 0, dMax = 0
  for (let i = 0; i < n; i++) {
    let pos = 0, neg = 0
    for (const s of series) {
      const v = s.values[i] ?? 0
      if (stacked) { if (v >= 0) pos += v; else neg += v } else { pos = Math.max(pos, v); neg = Math.min(neg, v) }
    }
    for (const l of lines) { const v = l.values[i]; if (v != null) { pos = Math.max(pos, v); neg = Math.min(neg, v) } }
    dMax = Math.max(dMax, pos); dMin = Math.min(dMin, neg)
  }
  const scale = niceScale(dMin, dMax)
  const fmtAxis = axisFormatter(scale.ticks, unit)
  const keys = [...series, ...lines].flatMap(s => categories.map((_, i) => `${s.id}|${i}`))
  const tween = useTweened(keys, everySeries.flatMap(s => categories.map((_, i) => s.values[i] ?? 0)))
  const dom = useTweenedDomain(scale.min, scale.max)

  const bandH = horizontal ? (stacked ? 30 : 14 * series.length + 14) : 0
  const height = p.height ?? (horizontal ? Math.max(120, n * bandH + 30) : 240)
  const title = (i: number) => p.categoryTitles?.[i] ?? categories[i] ?? ''
  const table = seriesTable(p.label, p.categoryHeading ?? (horizontal ? 'Voce' : 'Periodo'), categories.map((_, i) => title(i)), everySeries, unit)
  const total = (i: number) => series.reduce((a, s) => a + (s.values[i] ?? 0), 0)

  return (
    <ChartShell
      loading={p.loading} empty={empty} emptyTitle={p.emptyTitle} emptyDescription={p.emptyDescription}
      ariaLabel={summarize(p.label, categories, everySeries, unit)} table={table} height={height} vertical={horizontal}
      count={n} active={active} onActive={setActive} onLeave={() => { if (!touch.current) setActive(null) }}
      announce={active == null ? '' : `${title(active)}: ${everySeries.map(s => `${s.name} ${formatChartValue(s.values[active] ?? null, unit)}`).join(', ')}`}
    >
      {width => {
        const labelMax = Math.max(0, ...categories.map(c => textWidth(c)))
        const left = horizontal ? Math.min(Math.max(48, width * 0.4), labelMax + 8) : Math.max(...scale.ticks.map(t => textWidth(fmtAxis(t)))) + 8
        const right = horizontal ? 24 : PAD.right
        const plotW = Math.max(40, width - left - right)
        const top = PAD.top
        const plotH = height - top - PAD.bottom
        const range = dom.max - dom.min
        // Valore -> coordinata lungo l'asse dei valori, sempre dentro l'area del grafico.
        const clamp = (v: number) => Math.min(dom.max, Math.max(dom.min, v))
        const V = horizontal ? (v: number) => left + ((clamp(v) - dom.min) / range) * plotW : (v: number) => top + plotH - ((clamp(v) - dom.min) / range) * plotH
        const band = horizontal ? plotH / n : plotW / n
        const inner = Math.max(2, band * (horizontal ? 0.72 : 0.7))
        const barT = stacked ? inner : Math.max(2, (inner - (series.length - 1) * 2) / series.length)
        const catPos = (i: number) => (horizontal ? top : left) + band * i
        const shown = horizontal ? new Set(categories.map((_, i) => i)) : thinIndices(n, band, labelMax)
        const maxChars = Math.max(4, Math.floor((left - 8) / 6.4))
        const pos0 = V(0)

        const bars = categories.flatMap((_, i) => {
          let pos = 0, neg = 0
          return series.map((s, si) => {
            const v = tween[si * n + i] ?? 0
            let a: number, b: number
            if (stacked) { if (v >= 0) { a = pos; pos += v; b = pos } else { a = neg; neg += v; b = neg } }
            else { a = 0; b = v }
            const p1 = V(a), p2 = V(b)
            const lo = Math.min(p1, p2), len = Math.abs(p2 - p1)
            const off = catPos(i) + (band - inner) / 2 + (stacked ? 0 : si * (barT + 2))
            const rx = Math.min(3, barT / 2, len / 2)
            return horizontal
              ? <rect key={`${i}-${si}`} x={lo} y={off} width={len} height={barT} rx={rx} fill={colorVar(s.color, si)} />
              : <rect key={`${i}-${si}`} x={off} y={lo} width={barT} height={len} rx={rx} fill={colorVar(s.color, si)} />
          })
        })

        let tooltip = null
        if (active != null) {
          const cx_ = horizontal ? V(Math.max(0, total(active))) : catPos(active) + band / 2
          const cy_ = horizontal ? catPos(active) + band / 2 : Math.min(...everySeries.map((s, k) => { const v = k < series.length && stacked ? total(active) : (s.values[active] ?? 0); return V(Math.max(0, v)) }))
          const rows: TooltipRow[] = everySeries.map((s, k) => ({ label: s.name, value: formatChartValue(s.values[active] ?? null, unit), color: colorVar(s.color, k) }))
          if (stacked && series.length > 1) rows.push({ label: 'Totale', value: formatChartValue(total(active), unit), color: undefined })
          tooltip = <ChartTooltip x={cx_} y={cy_} width={width} title={title(active)} rows={rows} />
        }

        return (
          <>
            <svg className="md-chart__svg" width={width} height={height} aria-hidden="true">
              {active != null ? (
                horizontal
                  ? <rect x={0} y={catPos(active)} width={width} height={band} fill="hsl(var(--md-surface-container-high))" opacity={0.6} rx={6} />
                  : <rect x={catPos(active)} y={top} width={band} height={plotH} fill="hsl(var(--md-surface-container-high))" opacity={0.6} rx={6} />
              ) : null}
              {scale.ticks.filter(t => t >= dom.min - 1e-9 && t <= dom.max + 1e-9).map(t => (
                <g key={t}>
                  {horizontal ? (
                    <>
                      <line className={t === 0 ? 'md-chart__axis' : 'md-chart__grid'} x1={V(t)} x2={V(t)} y1={top} y2={top + plotH} />
                      <text className="md-chart__text" x={V(t)} y={height - 8} textAnchor="middle">{fmtAxis(t)}</text>
                    </>
                  ) : (
                    <>
                      <line className={t === 0 ? 'md-chart__axis' : 'md-chart__grid'} x1={left} x2={left + plotW} y1={V(t)} y2={V(t)} />
                      <text className="md-chart__text" x={left - 6} y={V(t)} textAnchor="end" dominantBaseline="central">{fmtAxis(t)}</text>
                    </>
                  )}
                </g>
              ))}
              {categories.map((c, i) => shown.has(i) ? (
                horizontal
                  ? <text key={i} className="md-chart__text" x={left - 8} y={catPos(i) + band / 2} textAnchor="end" dominantBaseline="central"><title>{c}</title>{clip(c, maxChars)}</text>
                  : <text key={i} className="md-chart__text" x={catPos(i) + band / 2} y={height - 8} textAnchor="middle">{c}</text>
              ) : null)}
              {bars}
              {lines.map((l, li) => {
                const col = colorVar(l.color, series.length + li)
                const base = (series.length + li) * n
                const segs: Array<Array<[number, number]>> = []
                let cur: Array<[number, number]> = []
                l.values.forEach((v, i) => {
                  if (i >= n) return
                  if (v == null) { if (cur.length) segs.push(cur); cur = [] } else cur.push([catPos(i) + band / 2, V(tween[base + i] ?? 0)])
                })
                if (cur.length) segs.push(cur)
                return (
                  <g key={l.id}>
                    {segs.map((sg, k) => <path key={k} d={sg.map((pt, j) => `${j ? 'L' : 'M'}${pt[0]},${pt[1]}`).join(' ')} fill="none" stroke={col} strokeWidth={2} strokeLinejoin="round" />)}
                    {segs.flat().map((pt, k) => <circle key={k} cx={pt[0]} cy={pt[1]} r={3} fill={col} stroke="hsl(var(--md-surface))" strokeWidth={1} />)}
                  </g>
                )
              })}
              {horizontal ? <line className="md-chart__axis" x1={pos0} x2={pos0} y1={top} y2={top + plotH} /> : null}
              <rect
                x={horizontal ? 0 : left} y={horizontal ? top : 0} width={horizontal ? width : plotW} height={horizontal ? plotH : height - PAD.bottom} fill="transparent"
                onPointerMove={e => { touch.current = e.pointerType === 'touch'; setActive(pointerIndex(e, !!horizontal, 0, horizontal ? plotH : plotW, n)) }}
                onPointerDown={e => { touch.current = e.pointerType === 'touch'; setActive(pointerIndex(e, !!horizontal, 0, horizontal ? plotH : plotW, n)) }}
              />
            </svg>
            {tooltip}
          </>
        )
      }}
    </ChartShell>
  )
}
